import { generateWindowFromReelStrips } from '../../helpers';

export type TFf036Jackpot = 'mini' | 'minor' | 'major' | 'grand';
export type TFf036CoinType = 'price' | TFf036Jackpot;
export type TFf036Cell = { reel: number; row: number };
export type TFf036SuperAwardClass = 'line' | 'collect' | 'hold-and-win';
export type TFf036BaseJokerVariant =
  'addPrizeCoin' | 'addVortex' | 'boostPrizeCoin' | 'completeCollection' | 'completeBonusTrigger';
export type TFf036HoldJokerVariant = 'addPrizeCoin' | 'addVortex' | 'addCellMultiplier';
export type TFf036JokerVariant = TFf036BaseJokerVariant | TFf036HoldJokerVariant;

export type TFf036JokerControl = {
  version: 1;
  activation?: 'activate' | 'skip';
  variant?: TFf036JokerVariant;
  target?: TFf036Cell;
  prizeMultiplier?: number;
  [key: string]: unknown;
};

export type TFf036SuperControl = {
  activation?: 'activate' | 'skip';
  awardClass?: TFf036SuperAwardClass;
};

export type TFf036SuperContinuation = {
  version: 1 | 2;
  reelIndexes?: number[];
  coinOverlays: TFf036CoinOverlay[];
  coinOverlaysPresent?: boolean;
  perceivedGrowthIncrementCells?: TFf036Cell[];
  reelIndexesAtTopLevel?: boolean;
  coinOverlaysAtTopLevel?: boolean;
  growthAtTopLevel?: boolean;
  preservedFields?: Record<string, unknown>;
  preservedControlFields?: Record<string, unknown>;
};

export type TFf036CoinOverlay = {
  reel: number;
  row: number;
  coinType: TFf036CoinType;
  multiplier?: number;
};

export type TFf036CollectedSource = {
  reel: number;
  row: number;
  occupantType: TFf036CoinType | 'vortex';
  effectiveMultiplier: number;
};

export type TFf036Event = {
  id: string;
  type: 'prize-coin' | 'fixed-jackpot-coin' | 'cell-multiplier' | 'vortex';
  reel: number;
  row: number;
  multiplier?: number;
  jackpotType?: TFf036Jackpot;
  expectedStoredBaseMultiplier?: number;
  expectedCollectedSources?: TFf036CollectedSource[];
};

export type TFf036Scenario = {
  randomBonus?: 'activate' | 'skip';
  version: 1 | 2;
  reelIndexes?: number[];
  coinOverlays?: TFf036CoinOverlay[];
  perceivedGrowthIncrementCells?: TFf036Cell[];
  superPsychoRespin?: TFf036SuperControl & {
    reelIndexes?: number[];
    coinOverlays?: TFf036CoinOverlay[];
    perceivedGrowthIncrementCells?: TFf036Cell[];
  };
  holdAndWin?: {
    version: 1;
    events: TFf036Event[];
    expectedTerminalReason?: 'active' | 'respins-exhausted' | 'full-grid';
  };
  jokerIntervention?: TFf036JokerControl;
  [key: string]: unknown;
};

export type TFf036BaseState = {
  randomBonus?: 'activate' | 'skip';
  version?: 1 | 2;
  reelIndexes: number[];
  coinOverlays: TFf036CoinOverlay[];
  coinOverlaysPresent?: boolean;
  perceivedGrowthIncrementCells?: TFf036Cell[];
  superPsychoRespin?: TFf036SuperControl;
  jokerIntervention?: TFf036JokerControl;
  preservedFields?: Record<string, unknown>;
  preservedSuperFields?: Record<string, unknown>;
};

export type TFf036FeatureCard = {
  events: TFf036Event[];
  jokerIntervention?: TFf036JokerControl;
  expectedTerminalReason?: 'active' | 'respins-exhausted' | 'full-grid';
  preservedFields?: Record<string, unknown>;
  preservedHoldFields?: Record<string, unknown>;
};

export type TFf036EditorState = {
  base: TFf036BaseState;
  superContinuation?: TFf036SuperContinuation;
  featureCards: TFf036FeatureCard[];
  unsupportedQueue?: TFf036Scenario[];
  unsupportedReason?: string;
};

export type TFf036BoardCoin = {
  coinType: TFf036CoinType;
  originalMultiplier: number;
  appliedCellMultiplier: number;
  effectiveMultiplier: number;
};

export type TFf036BoardVortex = {
  storedBaseMultiplier: number;
  appliedCellMultiplier: number;
  effectiveMultiplier: number;
};

export type TFf036BoardCell = {
  reel: number;
  row: number;
  occupantType: 'coin' | 'vortex' | null;
  coin: TFf036BoardCoin | null;
  vortex: TFf036BoardVortex | null;
  storedCellMultiplier: number;
};

export type TFf036Board = TFf036BoardCell[][];

export type TFf036Config = {
  reelsSetup: number[];
  baseSpinsReels: string[][];
  jackpotTotalBetMultipliers?: Partial<Record<TFf036Jackpot, number>>;
  coinFeature: {
    coin: string;
    coinTriggerSpinCount: number;
    coins: Array<number | TFf036Jackpot>;
    coinsOnTriggerWeights?: number[];
    multiplierIncrement: number;
    vortexEligibleCells: Array<{ reel: number; row: number }>;
    collectableOccupantTypes: Array<TFf036CoinType | 'vortex'>;
  };
  randomBonus?: { seedMultiplierWeights: { apply: number; skip: number } };
  perceivedGrowth?: { enabled: boolean; increment?: number; tierThresholds?: number[] };
  superPsychoRespin?: {
    awardClassWeights?: Partial<Record<TFf036SuperAwardClass, number>>;
  };
  jokerIntervention?: {
    version: 1;
    enabled: boolean;
    base: { variantWeights: Record<TFf036BaseJokerVariant, number>; prizeCoinWeights?: number[] };
    holdAndWin: { variantWeights: Record<TFf036HoldJokerVariant, number>; prizeCoinWeights?: number[] };
  };
  lines?: number[][];
  paytable?: Record<string, number[]>;
  wild?: string;
  symbolsReplacedByWild?: Record<string, boolean>;
};

const JACKPOTS: TFf036Jackpot[] = ['mini', 'minor', 'major', 'grand'];

export const cellKey = (cell: { reel: number; row: number }) => `${cell.reel}:${cell.row}`;
export const cellOrder = (cell: { reel: number; row: number }) => cell.reel * 3 + cell.row;
export const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const sortCells = <T extends TFf036Cell>(cells: T[]): T[] =>
  [...cells].sort((left, right) => cellOrder(left) - cellOrder(right));

export const toggleCell = (cells: TFf036Cell[], target: TFf036Cell): TFf036Cell[] => {
  const exists = cells.some(cell => cellKey(cell) === cellKey(target));
  return sortCells(exists
    ? cells.filter(cell => cellKey(cell) !== cellKey(target))
    : [...cells, { ...target }]);
};

export const getVisibleWindow = (reelIndexes: number[], config: TFf036Config): string[][] =>
  generateWindowFromReelStrips({
    reelIndexes,
    reelStrips: config.baseSpinsReels,
    reels: config.reelsSetup.length,
    rows: config.reelsSetup[0],
  });

export const getVisibleCoinCells = (window: string[][], config: TFf036Config) =>
  window.flatMap((reel, reelIndex) => reel.flatMap((symbol, row) =>
    symbol === config.coinFeature.coin ? [{ reel: reelIndex, row }] : []));

export const getVisibleVortexCells = (window: string[][]) =>
  window.flatMap((reel, reelIndex) => reel.flatMap((symbol, row) =>
    symbol === 'vortex' ? [{ reel: reelIndex, row }] : []));

export const getConfiguredPrizeMultipliers = (config: TFf036Config): number[] =>
  config.coinFeature.coins.filter((coin): coin is number => typeof coin === 'number');

export const getConfiguredJackpots = (config: TFf036Config): TFf036Jackpot[] =>
  config.coinFeature.coins.filter((coin): coin is TFf036Jackpot =>
    typeof coin === 'string' && JACKPOTS.includes(coin));

export const defaultCoinOverlay = (
  cell: { reel: number; row: number },
  config: TFf036Config,
): TFf036CoinOverlay => ({
  ...cell,
  coinType: 'price',
  multiplier: getConfiguredPrizeMultipliers(config)[0],
});

export const baseCoinValuesAreForced = (base: TFf036BaseState): boolean =>
  !!base.coinOverlaysPresent || base.coinOverlays.length > 0;

export const reconcileBaseCoins = (
  base: TFf036BaseState,
  config: TFf036Config,
): TFf036BaseState => {
  const visibleCells = getVisibleCoinCells(getVisibleWindow(base.reelIndexes, config), config);
  const visibleKeys = new Set(visibleCells.map(cellKey));
  const retained = base.coinOverlays.filter(coin => visibleKeys.has(cellKey(coin)));
  const retainedKeys = new Set(retained.map(cellKey));
  const forceCoinValues = baseCoinValuesAreForced(base);
  return {
    ...base,
    coinOverlays: forceCoinValues
      ? sortCells([
        ...retained,
        ...visibleCells.filter(cell => !retainedKeys.has(cellKey(cell))).map(cell => defaultCoinOverlay(cell, config)),
      ])
      : [],
    coinOverlaysPresent: forceCoinValues ? true : undefined,
  };
};

export const reconcileGrowthCells = (
  selected: TFf036Cell[],
  eligible: TFf036Cell[],
  config: TFf036Config,
): TFf036Cell[] => {
  if (config.perceivedGrowth?.enabled === false) return [];
  const eligibleKeys = new Set(eligible.map(cellKey));
  return sortCells(selected.filter(cell => eligibleKeys.has(cellKey(cell))));
};

export const isHoldAndWinTrigger = (base: TFf036BaseState, config: TFf036Config): boolean => {
  const window = getVisibleWindow(base.reelIndexes, config);
  if (getVisibleVortexCells(window).length === 0) return false;
  const coinCells = getVisibleCoinCells(window, config);
  return coinCells.some(coin => coin.reel === 0) && coinCells.some(coin => coin.reel === 2);
};

export type TFf036SearchKind = 'natural-trigger' | 'random-trigger' | 'losing' | TFf036SuperAwardClass;

const windowMatches = (window: string[][], config: TFf036Config, kind: TFf036SearchKind) => {
  const coins = getVisibleCoinCells(window, config);
  const vortices = getVisibleVortexCells(window);
  const trigger = vortices.length > 0 && coins.some(c => c.reel === 0) && coins.some(c => c.reel === 2);
  const collection = vortices.length > 0 && coins.length > 0;
  if (kind === 'natural-trigger' || kind === 'hold-and-win') return trigger;
  if (kind === 'random-trigger') return !trigger && coins.length + vortices.length > 0 && coins.length + vortices.length < 3;
  if (kind === 'collect') return collection && !trigger;
  if (kind === 'line') return !collection && hasWinningLine(window, config);
  return !collection && !hasWinningLine(window, config);
};

/** Searches actual strip stops, wrapping around after the current selection. */
export const findReelIndexes = (
  config: TFf036Config, kind: TFf036SearchKind, after?: number[],
): number[] | undefined => {
  const lengths = config.baseSpinsReels.map(reel => reel.length);
  const count = lengths.reduce((total, length) => total * length, 1);
  if (lengths.length !== 3 || !count) return undefined;
  const start = after ? (after[0] * lengths[1] + after[1]) * lengths[2] + after[2] + 1 : 0;
  for (let offset = 0; offset < count; offset++) {
    const n = (start + offset) % count;
    const indexes = [Math.floor(n / (lengths[1] * lengths[2])), Math.floor(n / lengths[2]) % lengths[1], n % lengths[2]];
    if (windowMatches(getVisibleWindow(indexes, config), config, kind)) return indexes;
  }
  return undefined;
};

export const getSuperContinuationWindow = (state: TFf036EditorState, config: TFf036Config): string[][] => {
  const indexes = state.superContinuation?.reelIndexes ??
    findReelIndexes(config, state.base.superPsychoRespin?.awardClass || 'line');
  if (!indexes) throw new Error('No configured reel outcome satisfies the selected Super Psycho award class.');
  return getVisibleWindow(indexes, config);
};

export type TFf036BaseJokerFeasibleVariant = {
  variant: TFf036BaseJokerVariant;
  kind: 'add-prize' | 'add-vortex' | 'boost-prize';
  targets: TFf036Cell[];
};

export const getBaseJokerFeasibleVariants = (
  base: TFf036BaseState,
  config: TFf036Config,
): TFf036BaseJokerFeasibleVariant[] => {
  const window = getVisibleWindow(base.reelIndexes, config);
  const outerNonCoins = [0, 2].flatMap(reel => [0, 1, 2]
    .filter(row => window[reel][row] !== config.coinFeature.coin)
    .map(row => ({ reel, row })));
  const eligibleVortexKeys = new Set(config.coinFeature.vortexEligibleCells.map(cellKey));
  const centerNonVortices = [0, 1, 2]
    .map(row => ({ reel: 1, row }))
    .filter(cell => window[1][cell.row] !== 'vortex' && eligibleVortexKeys.has(cellKey(cell)));
  const maximumPrize = getConfiguredPrizeMultipliers(config).slice(-1)[0];
  const boostable = base.coinOverlays
    .filter(coin => coin.coinType === 'price' && (coin.multiplier ?? 0) < maximumPrize)
    .map(({ reel, row }) => ({ reel, row }));
  const visibleCoins = getVisibleCoinCells(window, config);
  const left = visibleCoins.some(coin => coin.reel === 0);
  const right = visibleCoins.some(coin => coin.reel === 2);
  const vortex = window[1].includes('vortex');
  const collectionComplete = vortex && (left || right);
  const holdComplete = left && vortex && right;
  const feasible: TFf036BaseJokerFeasibleVariant[] = [];
  if (outerNonCoins.length) feasible.push({ variant: 'addPrizeCoin', kind: 'add-prize', targets: outerNonCoins });
  if (centerNonVortices.length) feasible.push({ variant: 'addVortex', kind: 'add-vortex', targets: centerNonVortices });
  if (boostable.length) feasible.push({ variant: 'boostPrizeCoin', kind: 'boost-prize', targets: boostable });
  if (!collectionComplete && !holdComplete) {
    if (vortex && !left && !right && outerNonCoins.length) {
      feasible.push({ variant: 'completeCollection', kind: 'add-prize', targets: outerNonCoins });
    } else if (!vortex && left !== right && centerNonVortices.length) {
      feasible.push({ variant: 'completeCollection', kind: 'add-vortex', targets: centerNonVortices });
    }
  }
  if (!holdComplete && Number(left) + Number(vortex) + Number(right) === 2) {
    if (!left) {
      const targets = outerNonCoins.filter(cell => cell.reel === 0);
      if (targets.length) feasible.push({ variant: 'completeBonusTrigger', kind: 'add-prize', targets });
    } else if (!right) {
      const targets = outerNonCoins.filter(cell => cell.reel === 2);
      if (targets.length) feasible.push({ variant: 'completeBonusTrigger', kind: 'add-prize', targets });
    } else if (centerNonVortices.length) {
      feasible.push({ variant: 'completeBonusTrigger', kind: 'add-vortex', targets: centerNonVortices });
    }
  }
  return feasible;
};

export type TFf036ProjectedBase = { window: string[][]; coinOverlays: TFf036CoinOverlay[]; multipliedCells?: TFf036Cell[] };

export const projectBaseJoker = (
  base: TFf036BaseState,
  config: TFf036Config,
): TFf036ProjectedBase => {
  const window = clone(getVisibleWindow(base.reelIndexes, config));
  const coinOverlays = baseCoinValuesAreForced(base)
    ? clone(base.coinOverlays)
    : getVisibleCoinCells(window, config).map(cell => defaultCoinOverlay(cell, config));
  const control = base.jokerIntervention;
  if (control?.activation !== 'activate' || config.jokerIntervention?.enabled === false) {
    return { window, coinOverlays };
  }
  const feasible = getBaseJokerFeasibleVariants(base, config);
  const selected = control.variant
    ? feasible.find(item => item.variant === control.variant)
    : feasible[0];
  if (!selected) return { window, coinOverlays };
  const target = control.target
    ? selected.targets.find(cell => cellKey(cell) === cellKey(control.target!))
    : selected.targets[0];
  if (!target) return { window, coinOverlays };
  if (selected.kind === 'add-vortex') {
    window[target.reel][target.row] = 'vortex';
  } else if (selected.kind === 'add-prize') {
    const multiplier = control.prizeMultiplier ?? getConfiguredPrizeMultipliers(config)[0];
    window[target.reel][target.row] = config.coinFeature.coin;
    coinOverlays.push({ ...target, coinType: 'price', multiplier });
  } else {
    const overlay = coinOverlays.find(coin => cellKey(coin) === cellKey(target));
    const prizes = getConfiguredPrizeMultipliers(config);
    const index = prizes.indexOf(overlay?.multiplier ?? NaN);
    if (overlay && index >= 0 && index < prizes.length - 1) overlay.multiplier = prizes[index + 1];
  }
  return { window, coinOverlays: sortCells(coinOverlays) };
};

export const projectedBaseTriggersHold = (base: TFf036BaseState, config: TFf036Config): boolean => {
  const projected = projectBaseJoker(base, config);
  const vortices = getVisibleVortexCells(projected.window);
  return vortices.length > 0 &&
    projected.coinOverlays.some(coin => coin.reel === 0) &&
    projected.coinOverlays.some(coin => coin.reel === 2);
};

const hasWinningLine = (window: string[][], config: TFf036Config): boolean => {
  if (!config.lines || !config.paytable) return false;
  return config.lines.some(line => {
    const symbols = line.map((row, reel) => window[reel][row]);
    const candidates = symbols.filter(symbol => symbol !== config.wild);
    const symbol = candidates[0] || config.wild;
    if (!symbol || !symbols.every(value => value === symbol ||
      (value === config.wild && config.symbolsReplacedByWild?.[symbol] !== false))) return false;
    const awards = config.paytable?.[symbol] || [];
    return awards.some(value => value > 0);
  });
};

export const isSuperPsychoForceEligible = (base: TFf036BaseState, config: TFf036Config): boolean => {
  if (base.randomBonus === 'activate' || base.jokerIntervention?.activation === 'activate') return false;
  const projected = projectBaseJoker(base, config);
  if (getVisibleVortexCells(projected.window).length > 0 && projected.coinOverlays.length > 0) return false;
  return !hasWinningLine(projected.window, config);
};

export const superContinuationSatisfiesAward = (
  state: TFf036EditorState,
  config: TFf036Config,
): boolean => {
  const award = state.base.superPsychoRespin?.awardClass;
  if (!state.superContinuation) return true;
  if (!award) {
    return state.superContinuation.reelIndexes === undefined &&
      !state.superContinuation.coinOverlaysPresent &&
      state.superContinuation.perceivedGrowthIncrementCells === undefined;
  }
  let window: string[][];
  try { window = getSuperContinuationWindow(state, config); } catch { return false; }
  if (award === 'line') return !config.lines || !config.paytable || windowMatches(window, config, 'line');
  const coinCells = getVisibleCoinCells(window, config);
  const vortex = getVisibleVortexCells(window).length > 0;
  const left = coinCells.some(coin => coin.reel === 0);
  const right = coinCells.some(coin => coin.reel === 2);
  return award === 'hold-and-win' ? vortex && left && right : vortex && (left || right) && !(left && right);
};

export const reconcileJokerControl = <T extends TFf036JokerControl>(
  control: T | undefined,
  feasible: Array<{ variant: TFf036JokerVariant; kind?: string; targets: TFf036Cell[] }>,
  config: TFf036Config,
): T | undefined => {
  if (!control) return undefined;
  if (control.activation === undefined) return clone(control);
  const {
    version: _version,
    activation: _activation,
    variant: _variant,
    target: _target,
    prizeMultiplier: _prizeMultiplier,
    ...preservedFields
  } = control;
  if (control.activation === 'skip') return { ...preservedFields, version: 1, activation: 'skip' } as T;
  if (config.jokerIntervention?.enabled === false) return undefined;
  if (feasible.length === 0) return undefined;
  const selected = control.variant ? feasible.find(item => item.variant === control.variant) : undefined;
  if (control.variant && !selected) return { ...preservedFields, version: 1, activation: 'activate' } as T;
  const target = selected && control.target && selected.targets.some(cell => cellKey(cell) === cellKey(control.target!))
    ? control.target
    : undefined;
  const acceptsPrize = selected?.kind === 'add-prize' || selected?.variant === 'addPrizeCoin';
  const prizeMultiplier = acceptsPrize && control.prizeMultiplier !== undefined &&
    getConfiguredPrizeMultipliers(config).includes(control.prizeMultiplier)
    ? control.prizeMultiplier
    : undefined;
  return {
    ...preservedFields,
    version: 1,
    activation: 'activate',
    ...(control.variant ? { variant: control.variant } : {}),
    ...(target ? { target: { ...target } } : {}),
    ...(prizeMultiplier !== undefined ? { prizeMultiplier } : {}),
  } as T;
};

export const isJokerControlProjectable = (
  control: TFf036JokerControl | undefined,
  feasible: Array<{ variant: TFf036JokerVariant; kind?: string; targets: TFf036Cell[] }>,
  config: TFf036Config,
): boolean => {
  if (!control || control.activation === undefined || control.activation === 'skip') return true;
  if (config.jokerIntervention?.enabled === false || feasible.length === 0) return false;
  if (!control.variant) return control.target === undefined && control.prizeMultiplier === undefined;
  const selected = feasible.find(item => item.variant === control.variant);
  if (!selected) return false;
  if (control.target && !selected.targets.some(cell => cellKey(cell) === cellKey(control.target!))) return false;
  const acceptsPrize = selected.kind === 'add-prize' || selected.variant === 'addPrizeCoin';
  return control.prizeMultiplier === undefined ||
    (acceptsPrize && getConfiguredPrizeMultipliers(config).includes(control.prizeMultiplier));
};

const jackpotMultiplier = (coinType: TFf036CoinType, config: TFf036Config): number =>
  coinType === 'price' ? 0 : config.jackpotTotalBetMultipliers?.[coinType] ?? 0;

const emptyBoard = (): TFf036Board => [0, 1, 2].map(reel => [0, 1, 2].map(row => ({
  reel,
  row,
  occupantType: null,
  coin: null,
  vortex: null,
  storedCellMultiplier: 0,
})));

const effectiveWithCellMultiplier = (original: number, applied: number) =>
  applied > 0 ? original * applied : original;

/** Collect each phase's Coins into every Vortex, then free the source cells. */
export const collectAndClear = (sourceBoard: TFf036Board): TFf036Board => {
  const board = clone(sourceBoard);
  const collectors = board.flat().filter(cell => cell.vortex);
  if (!collectors.length) return board;
  const total = board.flat().reduce((sum, cell) => sum + (cell.coin?.effectiveMultiplier ?? 0), 0);
  collectors.forEach(cell => {
    cell.vortex!.storedBaseMultiplier += total;
    cell.vortex!.effectiveMultiplier = cell.vortex!.storedBaseMultiplier;
  });
  board.flat().forEach(cell => {
    if (cell.coin) { cell.coin = null; cell.occupantType = null; }
  });
  return board;
};

const addSeedVortex = (board: TFf036Board, cell: TFf036Cell) => {
  board[cell.reel][cell.row].occupantType = 'vortex';
  board[cell.reel][cell.row].vortex = { storedBaseMultiplier: 0, appliedCellMultiplier: 0, effectiveMultiplier: 0 };
};

export const createSeedBoardFromWindow = (
  window: string[][],
  overlays: TFf036CoinOverlay[],
  config: TFf036Config,
  multipliedCells: TFf036Cell[] = [],
): TFf036Board => {
  const board = emptyBoard();
  overlays.forEach(overlay => {
    const originalMultiplier = overlay.coinType === 'price'
      ? overlay.multiplier ?? 0
      : jackpotMultiplier(overlay.coinType, config);
    const target = board[overlay.reel][overlay.row];
    target.occupantType = 'coin';
    target.coin = {
      coinType: overlay.coinType,
      originalMultiplier,
      appliedCellMultiplier: overlay.coinType === 'price' && multipliedCells.some(cell => cellKey(cell) === cellKey(overlay)) ? 2 : 0,
      effectiveMultiplier: originalMultiplier * (overlay.coinType === 'price' && multipliedCells.some(cell => cellKey(cell) === cellKey(overlay)) ? 2 : 1),
    };
  });
  getVisibleVortexCells(window)
    .sort((left, right) => cellOrder(left) - cellOrder(right))
    .forEach(cell => addSeedVortex(board, cell));
  return collectAndClear(board);
};

export const createSeedBoard = (base: TFf036BaseState, config: TFf036Config): TFf036Board =>
  createSeedBoardFromWindow(
    getVisibleWindow(base.reelIndexes, config),
    baseCoinValuesAreForced(base)
      ? base.coinOverlays
      : getVisibleCoinCells(getVisibleWindow(base.reelIndexes, config), config)
        .map(cell => defaultCoinOverlay(cell, config)),
    config,
  );

export const getFeatureSeed = (state: TFf036EditorState, config: TFf036Config): TFf036ProjectedBase => {
  if (state.base.superPsychoRespin?.activation === 'activate' &&
    state.base.superPsychoRespin.awardClass === 'hold-and-win' && state.superContinuation) {
    const window = getSuperContinuationWindow(state, config);
    return {
      window,
      coinOverlays: state.superContinuation.coinOverlaysPresent
        ? clone(state.superContinuation.coinOverlays)
        : getVisibleCoinCells(window, config).map(cell => defaultCoinOverlay(cell, config)),
    };
  }
  const seed = projectBaseJoker(state.base, config);
  if (state.base.randomBonus !== 'activate') return seed;
  if (!isRandomBonusEligible(state.base, config)) throw new Error('Random bonus needs a Coin or Vortex without a natural trigger.');
  const valueIndex = config.coinFeature.coinsOnTriggerWeights?.findIndex(weight => weight > 0) ?? 0;
  const value = config.coinFeature.coins[valueIndex];
  const multipliedCells: TFf036Cell[] = [];
  for (const reel of [0, 2]) {
    if (seed.coinOverlays.some(coin => coin.reel === reel)) continue;
    const cell = { reel, row: 0 };
    seed.window[reel][0] = config.coinFeature.coin;
    seed.coinOverlays.push(typeof value === 'number' ? { ...cell, coinType: 'price', multiplier: value } : { ...cell, coinType: value });
    if (typeof value === 'number' && config.randomBonus?.seedMultiplierWeights.skip === 0 && config.randomBonus.seedMultiplierWeights.apply > 0) multipliedCells.push(cell);
  }
  if (!getVisibleVortexCells(seed.window).length) seed.window[1][0] = 'vortex';
  return { ...seed, coinOverlays: sortCells(seed.coinOverlays), multipliedCells };
};

export const isRandomBonusEligible = (base: TFf036BaseState, config: TFf036Config) => {
  const seed = projectBaseJoker(base, config);
  return base.superPsychoRespin?.activation !== 'activate' && !projectedBaseTriggersHold(base, config) &&
    seed.coinOverlays.length + getVisibleVortexCells(seed.window).length > 0;
};

export const editorTriggersHold = (state: TFf036EditorState, config: TFf036Config): boolean => {
  if (state.base.superPsychoRespin?.activation === 'activate') {
    if (state.base.superPsychoRespin.awardClass !== 'hold-and-win' || !state.superContinuation) return false;
    const seed = getFeatureSeed(state, config);
    const coinCells = getVisibleCoinCells(seed.window, config);
    return getVisibleVortexCells(seed.window).length > 0 &&
      coinCells.some(coin => coin.reel === 0) && coinCells.some(coin => coin.reel === 2);
  }
  return state.base.randomBonus === 'activate' ? isRandomBonusEligible(state.base, config) : projectedBaseTriggersHold(state.base, config);
};

export const getBaseGrowthEligibleCells = (base: TFf036BaseState, config: TFf036Config): TFf036Cell[] => {
  const projected = projectBaseJoker(base, config);
  return sortCells([...projected.coinOverlays.map(({ reel, row }) => ({ reel, row })), ...getVisibleVortexCells(projected.window)]);
};

export type TFf036HoldJokerFeasibleVariant = {
  variant: TFf036HoldJokerVariant;
  kind: 'add-prize' | 'add-vortex' | 'cell-multiplier';
  targets: TFf036Cell[];
};

export const getHoldJokerFeasibleVariants = (
  board: TFf036Board,
  config: TFf036Config,
): TFf036HoldJokerFeasibleVariant[] => {
  const empty = board.flat().filter(cell => cell.occupantType === null)
    .map(({ reel, row }) => ({ reel, row }));
  const eligibleVortex = new Set(config.coinFeature.vortexEligibleCells.map(cellKey));
  const centerEmpty = empty.filter(cell => cell.reel === 1 && eligibleVortex.has(cellKey(cell)));
  const multiplierTargets = board.flat()
    .filter(cell => cell.reel !== 1 && (cell.occupantType === null || cell.coin?.coinType === 'price'))
    .map(({ reel, row }) => ({ reel, row }));
  const candidates: TFf036HoldJokerFeasibleVariant[] = [
    ...(empty.some(cell => cell.reel !== 1) ? [{ variant: 'addPrizeCoin' as const, kind: 'add-prize' as const, targets: empty.filter(cell => cell.reel !== 1) }] : []),
    ...(centerEmpty.length ? [{ variant: 'addVortex' as const, kind: 'add-vortex' as const, targets: centerEmpty }] : []),
    ...(multiplierTargets.length
      ? [{ variant: 'addCellMultiplier' as const, kind: 'cell-multiplier' as const, targets: multiplierTargets }]
      : []),
  ];
  return candidates;
};

export const applyHoldJoker = (
  sourceBoard: TFf036Board,
  control: TFf036JokerControl | undefined,
  config: TFf036Config,
): TFf036Board => {
  if (control?.activation !== 'activate' || config.jokerIntervention?.enabled === false) return sourceBoard;
  const feasible = getHoldJokerFeasibleVariants(sourceBoard, config);
  const selected = control.variant ? feasible.find(item => item.variant === control.variant) : feasible[0];
  if (!selected) return sourceBoard;
  const target = control.target
    ? selected.targets.find(cell => cellKey(cell) === cellKey(control.target!))
    : selected.targets[0];
  if (!target) return sourceBoard;
  const event: TFf036Event = selected.kind === 'add-prize'
    ? {
      id: 'ff036-editor:joker', type: 'prize-coin', ...target,
      multiplier: control.prizeMultiplier ?? getConfiguredPrizeMultipliers(config)[0],
    }
    : selected.kind === 'add-vortex'
      ? { id: 'ff036-editor:joker', type: 'vortex', ...target }
      : { id: 'ff036-editor:joker', type: 'cell-multiplier', ...target };
  return collectAndClear(applyEvent(sourceBoard, event, config));
};

const holdJokerResetsRespins = (
  board: TFf036Board,
  control: TFf036JokerControl | undefined,
  config: TFf036Config,
): boolean => {
  if (control?.activation !== 'activate' || config.jokerIntervention?.enabled === false) return false;
  const feasible = getHoldJokerFeasibleVariants(board, config);
  const selected = control.variant ? feasible.find(item => item.variant === control.variant) : feasible[0];
  return !!selected;
};

const eventsResetRespins = (events: readonly TFf036Event[]): boolean =>
  events.length > 0;

export const eventEvidence = (
  board: TFf036Board,
  event: TFf036Event,
  config: TFf036Config,
): Pick<TFf036Event, 'expectedStoredBaseMultiplier' | 'expectedCollectedSources'> => {
  if (event.type !== 'vortex') return {};
  return { expectedStoredBaseMultiplier: 0, expectedCollectedSources: [] };
};

export const applyEvent = (
  sourceBoard: TFf036Board,
  event: TFf036Event,
  config: TFf036Config,
): TFf036Board => {
  const board = clone(sourceBoard);
  const cell = board[event.reel][event.row];
  if (!isEventCompatible(event, cell, config)) {
    throw new Error(`Invalid ${event.type} event at ${cellKey(event)} for the projected cell state`);
  }
  const increment = config.coinFeature.multiplierIncrement;
  if (event.type === 'cell-multiplier') {
    if (cell.coin?.coinType === 'price') {
      cell.coin.appliedCellMultiplier += increment;
      cell.coin.effectiveMultiplier = effectiveWithCellMultiplier(
        cell.coin.originalMultiplier,
        cell.coin.appliedCellMultiplier,
      );
    } else if (cell.occupantType === null) {
      cell.storedCellMultiplier += increment;
    }
    return board;
  }
  if (cell.occupantType !== null) return board;
  if (event.type === 'vortex') {
    const evidence = eventEvidence(board, event, config);
    const original = evidence.expectedStoredBaseMultiplier ?? 0;
    if ((event.expectedStoredBaseMultiplier !== undefined && event.expectedStoredBaseMultiplier !== 0) ||
      (event.expectedCollectedSources?.length || 0) > 0) {
      throw new Error('A new Vortex starts at zero before phase collection.');
    }
    const applied = 0;
    cell.occupantType = 'vortex';
    cell.vortex = {
      storedBaseMultiplier: original,
      appliedCellMultiplier: applied,
      effectiveMultiplier: effectiveWithCellMultiplier(original, applied),
    };
    cell.storedCellMultiplier = 0;
    return board;
  }
  const coinType: TFf036CoinType = event.type === 'prize-coin' ? 'price' : event.jackpotType!;
  const original = coinType === 'price' ? event.multiplier ?? 0 : jackpotMultiplier(coinType, config);
  const applied = cell.storedCellMultiplier;
  cell.occupantType = 'coin';
  cell.coin = {
    coinType,
    originalMultiplier: original,
    appliedCellMultiplier: coinType === 'price' ? applied : 0,
    effectiveMultiplier: coinType === 'price' ? effectiveWithCellMultiplier(original, applied) : original,
  };
  cell.storedCellMultiplier = 0;
  return board;
};

export const applyCard = (
  board: TFf036Board,
  card: TFf036FeatureCard,
  config: TFf036Config,
): TFf036Board => collectAndClear(card.events.reduce(
  (current, event) => applyEvent(current, event, config),
  board,
));

export type TFf036TerminalReason = 'active' | 'respins-exhausted' | 'full-grid';

export type TFf036FeatureStep = {
  board: TFf036Board;
  respinsRemaining: number;
  terminalReason: TFf036TerminalReason;
};

export const projectFeatureSteps = (
  state: TFf036EditorState,
  config: TFf036Config,
): TFf036FeatureStep[] => {
  const seed = getFeatureSeed(state, config);
  const steps: TFf036FeatureStep[] = [{
    board: createSeedBoardFromWindow(seed.window, seed.coinOverlays, config, seed.multipliedCells),
    respinsRemaining: config.coinFeature.coinTriggerSpinCount,
    terminalReason: 'active',
  }];
  state.featureCards.forEach(card => {
    const previous = steps[steps.length - 1];
    if (previous.terminalReason !== 'active') {
      throw new Error(`Coin Feature Spin cannot follow ${previous.terminalReason}`);
    }
    const naturalBoard = applyCard(previous.board, card, config);
    const board = applyHoldJoker(naturalBoard, card.jokerIntervention, config);
    const jokerResets = holdJokerResetsRespins(naturalBoard, card.jokerIntervention, config);
    const respinsRemaining = eventsResetRespins(card.events) || jokerResets
      ? config.coinFeature.coinTriggerSpinCount
      : previous.respinsRemaining - 1;
    steps.push({
      board,
      respinsRemaining,
      terminalReason: respinsRemaining === 0 ? 'respins-exhausted' : 'active',
    });
  });
  return steps;
};

export const projectBoards = (state: TFf036EditorState, config: TFf036Config): TFf036Board[] => {
  return projectFeatureSteps(state, config).map(step => step.board);
};

export const isEventCompatible = (
  event: Pick<TFf036Event, 'type' | 'reel' | 'row' | 'multiplier' | 'jackpotType'>,
  cell: TFf036BoardCell,
  config: TFf036Config,
): boolean => {
  if (event.type === 'cell-multiplier') {
    return cell.reel !== 1 && (cell.occupantType === null || cell.coin?.coinType === 'price');
  }
  if (cell.occupantType !== null) return false;
  if (event.type === 'prize-coin') {
    return cell.reel !== 1 && getConfiguredPrizeMultipliers(config).includes(event.multiplier ?? NaN);
  }
  if (event.type === 'fixed-jackpot-coin') {
    return cell.reel !== 1 &&
      getConfiguredJackpots(config).includes(event.jackpotType as TFf036Jackpot);
  }
  return config.coinFeature.vortexEligibleCells.some(candidate => cellKey(candidate) === cellKey(cell));
};

export const getValidOutcomes = (
  cell: TFf036BoardCell,
  config: TFf036Config,
): Array<'nothing' | 'prize-coin' | 'fixed-jackpot-coin' | 'cell-multiplier' | 'vortex'> => {
  const outcomes: Array<'nothing' | 'prize-coin' | 'fixed-jackpot-coin' | 'cell-multiplier' | 'vortex'> = ['nothing'];
  if (cell.occupantType === null) {
    if (cell.reel !== 1 && getConfiguredPrizeMultipliers(config).length) outcomes.push('prize-coin');
    if (cell.reel !== 1 && getConfiguredJackpots(config).length) {
      outcomes.push('fixed-jackpot-coin');
    }
    if (cell.reel !== 1) outcomes.push('cell-multiplier');
    if (config.coinFeature.vortexEligibleCells.some(candidate => cellKey(candidate) === cellKey(cell))) {
      outcomes.push('vortex');
    }
  } else if (cell.reel !== 1 && cell.coin?.coinType === 'price') {
    outcomes.push('cell-multiplier');
  }
  return outcomes;
};

export const deterministicEventId = (cardIndex: number, cell: { reel: number; row: number }) =>
  `ff036-editor:${cardIndex + 1}:${cell.reel}:${cell.row}`;

export const canAddEventAt = (
  card: TFf036FeatureCard,
  cell: { reel: number; row: number },
) => card.events.some(event => cellKey(event) === cellKey(cell)) ||
  card.events.every(event => cellOrder(event) < cellOrder(cell));

const refreshVortexEvidence = (
  events: TFf036Event[],
  boardBeforeCard: TFf036Board,
  config: TFf036Config,
): TFf036Event[] => {
  let board = clone(boardBeforeCard);
  return events.map(event => {
    const refreshed = event.type === 'vortex'
      ? { ...event, ...eventEvidence(board, event, config) }
      : event;
    board = applyEvent(board, refreshed, config);
    return refreshed;
  });
};

export const reconcileFeatureEvidence = (
  state: TFf036EditorState,
  config: TFf036Config,
): TFf036EditorState => {
  const seed = getFeatureSeed(state, config);
  let step: TFf036FeatureStep = {
    board: createSeedBoardFromWindow(seed.window, seed.coinOverlays, config, seed.multipliedCells),
    respinsRemaining: config.coinFeature.coinTriggerSpinCount,
    terminalReason: 'active',
  };
  const featureCards: TFf036FeatureCard[] = [];
  for (const card of state.featureCards) {
    if (step.terminalReason !== 'active') break;
    let board = clone(step.board);
    const events: TFf036Event[] = [];
    card.events.forEach(event => {
      const cell = board[event.reel]?.[event.row];
      if (!cell || !isEventCompatible(event, cell, config)) return;
      const refreshed = event.type === 'vortex'
        ? { ...event, ...eventEvidence(board, event, config) }
        : event;
      events.push(refreshed);
      board = applyEvent(board, refreshed, config);
    });
    board = collectAndClear(board);
    const feasibleJokers = getHoldJokerFeasibleVariants(board, config);
    const jokerIntervention = reconcileJokerControl(card.jokerIntervention, feasibleJokers, config);
    const jokerResets = holdJokerResetsRespins(board, jokerIntervention, config);
    board = applyHoldJoker(board, jokerIntervention, config);
    const respinsRemaining = eventsResetRespins(events) || jokerResets
      ? config.coinFeature.coinTriggerSpinCount
      : step.respinsRemaining - 1;
    const terminalReason: TFf036TerminalReason = respinsRemaining === 0 ? 'respins-exhausted' : 'active';
    featureCards.push({
      ...card,
      events,
      ...(jokerIntervention ? { jokerIntervention } : { jokerIntervention: undefined }),
      ...(card.expectedTerminalReason ? { expectedTerminalReason: terminalReason } : {}),
    });
    step = { board, respinsRemaining, terminalReason };
  }
  return { ...state, featureCards };
};

export const addOrReplaceEvent = (props: {
  card: TFf036FeatureCard;
  cardIndex: number;
  boardBeforeCard: TFf036Board;
  cell: { reel: number; row: number };
  outcome: 'nothing' | 'prize-coin' | 'fixed-jackpot-coin' | 'cell-multiplier' | 'vortex';
  value?: number | TFf036Jackpot;
  config: TFf036Config;
}): TFf036FeatureCard => {
  const withoutTarget = props.card.events.filter(event => cellKey(event) !== cellKey(props.cell));
  if (props.outcome === 'nothing') return {
    ...props.card,
    events: refreshVortexEvidence(withoutTarget, props.boardBeforeCard, props.config),
  };
  if (!canAddEventAt(props.card, props.cell)) {
    throw new Error('FF036 Hold & Win events must be selected in reel-major order');
  }
  const base: TFf036Event = {
    id: deterministicEventId(props.cardIndex, props.cell),
    type: props.outcome,
    reel: props.cell.reel,
    row: props.cell.row,
  };
  const event: TFf036Event = props.outcome === 'prize-coin'
    ? { ...base, multiplier: Number(props.value ?? getConfiguredPrizeMultipliers(props.config)[0]) }
    : props.outcome === 'fixed-jackpot-coin'
      ? { ...base, jackpotType: (props.value ?? getConfiguredJackpots(props.config)[0]) as TFf036Jackpot }
      : base;
  const events = [...withoutTarget, event].sort((left, right) => cellOrder(left) - cellOrder(right));
  return {
    ...props.card,
    events: refreshVortexEvidence(events, props.boardBeforeCard, props.config),
  };
};

export const reconcileSuperContinuation = (
  continuation: TFf036SuperContinuation,
  state: Pick<TFf036EditorState, 'base' | 'superContinuation'>,
  config: TFf036Config,
): TFf036SuperContinuation => {
  if (!state.base.superPsychoRespin?.awardClass) return continuation;
  const window = continuation.reelIndexes
    ? getVisibleWindow(continuation.reelIndexes, config)
    : getSuperContinuationWindow({ ...state, superContinuation: continuation, featureCards: [] }, config);
  const visibleCoins = getVisibleCoinCells(window, config);
  const visibleKeys = new Set(visibleCoins.map(cellKey));
  const retained = continuation.coinOverlays.filter(coin => visibleKeys.has(cellKey(coin)));
  const retainedKeys = new Set(retained.map(cellKey));
  const forceCoinValues = !!continuation.coinOverlaysPresent || continuation.coinOverlays.length > 0;
  const coinOverlays = forceCoinValues
    ? sortCells([
      ...retained,
      ...visibleCoins.filter(cell => !retainedKeys.has(cellKey(cell))).map(cell => defaultCoinOverlay(cell, config)),
    ])
    : [];
  return {
    ...continuation,
    coinOverlays,
    coinOverlaysPresent: forceCoinValues ? true : undefined,
    ...(continuation.perceivedGrowthIncrementCells === undefined ? {} : {
      perceivedGrowthIncrementCells: reconcileGrowthCells(
        continuation.perceivedGrowthIncrementCells,
        [],
        config,
      ),
    }),
  };
};

export const createSuperContinuation = (
  base: TFf036BaseState,
  config: TFf036Config,
): TFf036SuperContinuation => reconcileSuperContinuation({
  version: base.superPsychoRespin?.awardClass === 'hold-and-win' ? 2 : 1,
  ...(base.superPsychoRespin?.awardClass ? { reelIndexes: findReelIndexes(config, base.superPsychoRespin.awardClass) } : {}),
  coinOverlays: [],
}, { base, superContinuation: undefined }, config);

export const serializeScenarioQueue = (state: TFf036EditorState): TFf036Scenario[] => {
  if (state.unsupportedQueue) return clone(state.unsupportedQueue);
  const base: TFf036Scenario = {
    ...(state.base.preservedFields || {}),
    version: state.base.jokerIntervention || state.base.randomBonus ? 2 : state.base.version || 1,
    ...(state.base.randomBonus ? { randomBonus: state.base.randomBonus } : {}),
    reelIndexes: [...state.base.reelIndexes],
    ...(state.base.coinOverlays.length || state.base.coinOverlaysPresent
      ? { coinOverlays: clone(state.base.coinOverlays) }
      : {}),
    ...(state.base.perceivedGrowthIncrementCells === undefined ? {} : {
      perceivedGrowthIncrementCells: clone(state.base.perceivedGrowthIncrementCells),
    }),
    ...(state.base.superPsychoRespin ? {
      superPsychoRespin: {
        ...(state.base.preservedSuperFields || {}),
        ...clone(state.base.superPsychoRespin),
      },
    } : {}),
    ...(state.base.jokerIntervention ? { jokerIntervention: clone(state.base.jokerIntervention) } : {}),
  };
  const continuation: TFf036Scenario[] = state.superContinuation ? [{
    ...(state.superContinuation.preservedFields || {}),
    version: state.superContinuation.version,
    ...(state.superContinuation.reelIndexesAtTopLevel && state.superContinuation.reelIndexes
      ? { reelIndexes: [...state.superContinuation.reelIndexes] }
      : {}),
    ...(state.superContinuation.coinOverlaysAtTopLevel &&
      (state.superContinuation.coinOverlays.length || state.superContinuation.coinOverlaysPresent)
      ? { coinOverlays: clone(state.superContinuation.coinOverlays) }
      : {}),
    ...(state.superContinuation.growthAtTopLevel &&
      state.superContinuation.perceivedGrowthIncrementCells !== undefined
      ? { perceivedGrowthIncrementCells: clone(state.superContinuation.perceivedGrowthIncrementCells) }
      : {}),
    superPsychoRespin: {
      ...(state.superContinuation.preservedControlFields || {}),
      ...(!state.superContinuation.reelIndexesAtTopLevel && state.superContinuation.reelIndexes
        ? { reelIndexes: [...state.superContinuation.reelIndexes] }
        : {}),
      ...(!state.superContinuation.coinOverlaysAtTopLevel &&
        (state.superContinuation.coinOverlays.length || state.superContinuation.coinOverlaysPresent)
        ? { coinOverlays: clone(state.superContinuation.coinOverlays) }
        : {}),
      ...(state.superContinuation.growthAtTopLevel ||
        state.superContinuation.perceivedGrowthIncrementCells === undefined ? {} : {
        perceivedGrowthIncrementCells: clone(state.superContinuation.perceivedGrowthIncrementCells),
      }),
    },
  }] : [];
  return [base, ...continuation, ...state.featureCards.map(card => ({
    ...(card.preservedFields || {}),
    version: 2 as const,
    holdAndWin: {
      ...(card.preservedHoldFields || {}),
      version: 1 as const,
      events: clone(card.events),
        ...(card.expectedTerminalReason ? { expectedTerminalReason: card.expectedTerminalReason } : {}),
    },
    ...(card.jokerIntervention ? { jokerIntervention: clone(card.jokerIntervention) } : {}),
  }))];
};

export const deserializeScenarioQueue = (
  scenarios: TFf036Scenario[],
  config?: TFf036Config,
): TFf036EditorState => {
  const first = clone(scenarios[0] || { version: 1, reelIndexes: [0, 0, 0] });
  const {
    version: baseVersion,
    randomBonus,
    reelIndexes = [0, 0, 0],
    coinOverlays = [],
    perceivedGrowthIncrementCells,
    superPsychoRespin,
    jokerIntervention,
    holdAndWin: unexpectedBaseHold,
    ...preservedBase
  } = first;
  const {
    activation: superActivation,
    awardClass,
    reelIndexes: unexpectedSuperReels,
    coinOverlays: unexpectedSuperCoins,
    perceivedGrowthIncrementCells: unexpectedSuperGrowth,
    ...preservedSuperFields
  } = superPsychoRespin || {};
  const parsedBase: TFf036BaseState = {
    version: baseVersion,
    ...(randomBonus ? { randomBonus } : {}),
    reelIndexes: [...reelIndexes],
    coinOverlays: clone(coinOverlays),
    ...(first.coinOverlays !== undefined ? { coinOverlaysPresent: true } : {}),
    ...(perceivedGrowthIncrementCells !== undefined
      ? { perceivedGrowthIncrementCells: clone(perceivedGrowthIncrementCells) }
      : {}),
    ...(superPsychoRespin ? {
      superPsychoRespin: {
        ...(superActivation ? { activation: superActivation } : {}),
        ...(awardClass ? { awardClass } : {}),
      },
    } : {}),
    ...(jokerIntervention ? { jokerIntervention: clone(jokerIntervention) } : {}),
    ...(Object.keys(preservedBase).length ? { preservedFields: preservedBase } : {}),
    ...(Object.keys(preservedSuperFields).length ? { preservedSuperFields } : {}),
  };
  const hasInvalidBaseControls = (randomBonus !== undefined && !['activate', 'skip'].includes(randomBonus)) || !!unexpectedBaseHold || unexpectedSuperReels !== undefined ||
    unexpectedSuperCoins !== undefined || unexpectedSuperGrowth !== undefined;
  let continuation: TFf036SuperContinuation | undefined;
  let continuationCount = 0;
  const possibleContinuation = scenarios[1];
  if (possibleContinuation?.superPsychoRespin &&
    possibleContinuation.superPsychoRespin.activation === undefined &&
    possibleContinuation.superPsychoRespin.awardClass === undefined &&
    !possibleContinuation.holdAndWin && !possibleContinuation.jokerIntervention) {
    const copiedContinuation = clone(possibleContinuation);
    const {
      version,
      superPsychoRespin: continuationControl,
      reelIndexes: topLevelReels,
      coinOverlays: topLevelCoins,
      perceivedGrowthIncrementCells: topLevelGrowth,
      ...preservedContinuation
    } = copiedContinuation;
    const {
      reelIndexes: continuationReels,
      coinOverlays: continuationCoins = [],
      perceivedGrowthIncrementCells: continuationGrowth,
      ...preservedControlFields
    } = continuationControl!;
    continuation = {
      version,
      ...((continuationReels || topLevelReels)
        ? { reelIndexes: [...(continuationReels || topLevelReels)!] }
        : {}),
      coinOverlays: clone(continuationControl!.coinOverlays ?? topLevelCoins ?? continuationCoins),
      ...(continuationControl!.coinOverlays !== undefined || topLevelCoins !== undefined
        ? { coinOverlaysPresent: true }
        : {}),
      ...((continuationGrowth ?? topLevelGrowth) !== undefined
        ? { perceivedGrowthIncrementCells: clone(continuationGrowth ?? topLevelGrowth)! }
        : {}),
      ...(continuationReels === undefined && topLevelReels !== undefined ? { reelIndexesAtTopLevel: true } : {}),
      ...(continuationControl!.coinOverlays === undefined && topLevelCoins !== undefined
        ? { coinOverlaysAtTopLevel: true }
        : {}),
      ...(continuationGrowth === undefined && topLevelGrowth !== undefined ? { growthAtTopLevel: true } : {}),
      ...(Object.keys(preservedContinuation).length ? { preservedFields: preservedContinuation } : {}),
      ...(Object.keys(preservedControlFields).length ? { preservedControlFields } : {}),
    };
    continuationCount = 1;
  }
  const remaining = scenarios.slice(1 + continuationCount);
  const unsupportedEntry = remaining.findIndex(scenario => !scenario.holdAndWin || !!scenario.superPsychoRespin);
  const continuationWithoutForcedActivation = !!continuation && superActivation !== 'activate';
  const missingSuperContinuationBeforeHold = superActivation === 'activate' && !continuation && remaining.length > 0;
  const holdAfterNonHoldSuper = !!continuation && remaining.length > 0 && awardClass !== 'hold-and-win';
  if (!first.reelIndexes || hasInvalidBaseControls || continuationWithoutForcedActivation ||
    missingSuperContinuationBeforeHold || holdAfterNonHoldSuper || unsupportedEntry >= 0) {
    const offset = 2 + continuationCount;
    return {
      base: parsedBase,
      featureCards: [],
      unsupportedQueue: clone(scenarios),
      unsupportedReason: !first.reelIndexes
        ? 'The first queue entry is not an editable Base Spin.'
        : hasInvalidBaseControls
          ? 'The Base Spin contains continuation-only Super Psycho or Hold & Win controls.'
          : continuationWithoutForcedActivation
            ? 'A Super Psycho continuation is only safely editable after forced activation.'
            : missingSuperContinuationBeforeHold
              ? 'Hold & Win controls cannot run before the forced Super Psycho continuation.'
              : holdAfterNonHoldSuper
                ? 'Hold & Win controls require the forced Super Psycho hold-and-win award class.'
                : `Queue entry ${unsupportedEntry + offset} is not a Hold & Win continuation or forced Super Psycho continuation.`,
    };
  }
  const editable: TFf036EditorState = {
    base: parsedBase,
    ...(continuation ? { superContinuation: continuation } : {}),
    featureCards: remaining.map(raw => {
      const scenario = clone(raw);
      const { version: _version, holdAndWin, jokerIntervention: cardJoker, ...preservedFields } = scenario;
      const {
        version: _holdVersion,
        events = [],
        expectedTerminalReason,
        ...preservedHoldFields
      } = holdAndWin!;
      return {
        events: clone(events),
        ...(cardJoker ? { jokerIntervention: clone(cardJoker) } : {}),
        ...(expectedTerminalReason
          ? { expectedTerminalReason }
          : {}),
        ...(Object.keys(preservedFields).length ? { preservedFields } : {}),
        ...(Object.keys(preservedHoldFields).length ? { preservedHoldFields } : {}),
      };
    }),
  };
  const hasInvalidSuperGrowth = (editable.superContinuation?.perceivedGrowthIncrementCells?.length || 0) > 0;
  const hasDisabledGrowth = config?.perceivedGrowth?.enabled === false && (
    (editable.base.perceivedGrowthIncrementCells?.length || 0) > 0 ||
    (editable.superContinuation?.perceivedGrowthIncrementCells?.length || 0) > 0
  );
  const hasDisabledJoker = config?.jokerIntervention?.enabled === false && (
    editable.base.jokerIntervention?.activation === 'activate' ||
    editable.featureCards.some(card => card.jokerIntervention?.activation === 'activate')
  );
  const hasIneligibleSuper = !!config && editable.base.superPsychoRespin?.activation === 'activate' &&
    !isSuperPsychoForceEligible(editable.base, config);
  const hasInvalidSuperOutcome = !!config && !superContinuationSatisfiesAward(editable, config);
  const hasIncompleteForcedSuperCoins = !!config && !!editable.superContinuation?.coinOverlaysPresent && (() => {
    if (hasInvalidSuperOutcome) return true;
    const visible = getVisibleCoinCells(getSuperContinuationWindow(editable, config), config).map(cellKey).sort();
    const forced = editable.superContinuation!.coinOverlays.map(cellKey).sort();
    return JSON.stringify(visible) !== JSON.stringify(forced);
  })();
  const hasIncompleteForcedBaseCoins = !!config && baseCoinValuesAreForced(editable.base) && (() => {
    const visible = getVisibleCoinCells(getVisibleWindow(editable.base.reelIndexes, config), config)
      .map(cellKey).sort();
    const forced = editable.base.coinOverlays.map(cellKey).sort();
    return JSON.stringify(visible) !== JSON.stringify(forced);
  })();
  const hasUnprojectableNaturalSuperSeed = editable.featureCards.length > 0 &&
    editable.base.superPsychoRespin?.awardClass === 'hold-and-win' &&
    !!editable.superContinuation && (!editable.superContinuation.coinOverlaysPresent || !editable.superContinuation.reelIndexes);
  const superOwnsFeatureSeed = editable.base.superPsychoRespin?.activation === 'activate' &&
    editable.base.superPsychoRespin.awardClass === 'hold-and-win' && !!editable.superContinuation;
  const hasUnprojectableNaturalBaseSeed = editable.featureCards.length > 0 && !superOwnsFeatureSeed &&
    !baseCoinValuesAreForced(editable.base);
  const hasUnsafeProjection = !!config && (() => {
    if (editable.base.randomBonus === 'activate' && !isRandomBonusEligible(editable.base, config)) return true;
    if (!isJokerControlProjectable(
      editable.base.jokerIntervention,
      getBaseJokerFeasibleVariants(editable.base, config),
      config,
    )) return true;
    if (editable.featureCards.length > 0 && !editorTriggersHold(editable, config)) return true;
    try {
      const steps = projectFeatureSteps(editable, config);
      return editable.featureCards.some((card, index) => {
        const naturalBoard = applyCard(steps[index].board, card, config);
        const feasible = getHoldJokerFeasibleVariants(naturalBoard, config);
        return !isJokerControlProjectable(card.jokerIntervention, feasible, config) ||
          (!!card.expectedTerminalReason && card.expectedTerminalReason !== steps[index + 1].terminalReason);
      });
    } catch (_caught) {
      return true;
    }
  })();
  if (hasInvalidSuperGrowth || hasDisabledGrowth || hasDisabledJoker || hasIneligibleSuper || hasInvalidSuperOutcome ||
    hasIncompleteForcedBaseCoins || hasIncompleteForcedSuperCoins || hasUnprojectableNaturalBaseSeed ||
    hasUnprojectableNaturalSuperSeed || hasUnsafeProjection) {
    return {
      base: parsedBase,
      featureCards: [],
      unsupportedQueue: clone(scenarios),
      unsupportedReason: hasInvalidSuperGrowth
        ? 'Super Psycho continuations preserve perceived growth and cannot increment it.'
        : hasDisabledGrowth
        ? 'This preset forces perceived growth while it is disabled by configuration.'
        : hasDisabledJoker
          ? 'This preset forces Joker Intervention while it is disabled by configuration.'
          : hasIneligibleSuper
            ? 'This preset forces Super Psycho Respin from an ineligible Base outcome.'
            : hasInvalidSuperOutcome
              ? 'This preset Super Psycho continuation does not satisfy its selected award class.'
              : hasIncompleteForcedBaseCoins
                ? 'This preset does not specify every visible Base Coin value.'
                : hasIncompleteForcedSuperCoins
                  ? 'This preset does not specify every visible Super Psycho Coin value.'
                  : hasUnprojectableNaturalBaseSeed
                    ? 'Hold & Win cards after natural Base Coin values cannot be projected safely.'
                    : hasUnprojectableNaturalSuperSeed
                      ? 'Hold & Win cards require explicit Super Psycho reel stops and Coin values for a reliable preview.'
                      : 'This preset depends on a queue transition that cannot be projected safely.',
    };
  }
  return editable;
};

export const createBlankState = (config: TFf036Config): TFf036EditorState => ({
  base: reconcileBaseCoins({
    version: 1,
    reelIndexes: config.reelsSetup.map(() => 0),
    coinOverlays: [],
    coinOverlaysPresent: true,
  }, config),
  featureCards: [],
});

export const isFullGrid = (board: TFf036Board) =>
  board.flat().every(cell => cell.occupantType !== null);
